import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider, theme, App as AntApp, message } from 'antd';
import zhCN from 'antd/lib/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import './App.css';
import routesConfig from './config/pro-routes.json';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginGreeting from './components/LoginGreeting';
import { isMobileDevice } from './utils/deviceDetector';

dayjs.locale('zh-cn');

declare global {
  interface Window {
    installPromptShown?: boolean;
    deferredPrompt?: any;
  }
}

const componentCache = new Map<string, React.LazyExoticComponent<any>>();

const lazyImport = (path: string) => {
  if (!componentCache.has(path)) {
    componentCache.set(path, React.lazy(() => import(`./${path}`)));
  }
  return componentCache.get(path)!;
};

// 移动端组件映射
const mobileComponentMap: Record<string, string> = {
  'pages/SafetyKnowledge/Admin/SafetyKnowledgeAdminList': 'pages/SafetyKnowledge/Admin/MobileSafetyKnowledgeAdminList',
  'pages/SafetyKnowledge/User/SafetyKnowledgeBrowseList': 'pages/SafetyKnowledge/User/MobileSafetyKnowledgeBrowseList',
  'pages/BoothStructureSafetyRequirement/Admin/BoothStructureSafetyRequirementAdminList': 'pages/BoothStructureSafetyRequirement/Admin/MobileBoothStructureSafetyRequirementAdminList'
};

const getComponentPath = (path: string, isMobile: boolean): string => {
  if (isMobile && mobileComponentMap[path]) {
    return mobileComponentMap[path];
  }
  return path;
};

const preloadMobileComponents = () => {
  const mobileComponents = [
    'pages/ExhibitorInfo',
    'pages/QuickRectification/QuickRectificationPage',
    'pages/ExhibitorInfo/AddingRectificationPage',
    'pages/ExhibitorInfo/Query/SpecialCollectionQueryMobile',
    'pages/SafetyKnowledge/User/SafetyKnowledgeBrowseList',
    'pages/SafetyKnowledge/Admin/MobileSafetyKnowledgeAdminList'
  ];

  mobileComponents.forEach(component => {
    lazyImport(component);
  });
};

// 创建 ProtectedRoute 组件
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  console.log('ProtectedRoute - isAuthenticated:', isAuthenticated);

  if (!isAuthenticated) {
    console.log('Redirecting to login from ProtectedRoute');
    // 保存用户原本要访问的路径
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }

  return <>{children}</>;
};

// 主应用组件
const AppContent: React.FC = () => {
  const [isMobile, setIsMobile] = React.useState(isMobileDevice());

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Router>
      <LoginGreeting />
      <Routes>
        {routesConfig.routes.map((route: any) => {
          // 处理重定向路由
          if (route.redirect && route.redirectTo) {
            return (
              <Route
                key={route.path}
                path={route.path}
                element={<Navigate to={route.redirectTo} replace />}
              />
            );
          }

          const componentPath = getComponentPath(route.component, isMobile);
          const Component = lazyImport(componentPath);
          let element;
          
          if (route.protected) {
            element = (
              <ProtectedRoute>
                <React.Suspense fallback={<div className="loading-screen"><div className="loading-spinner"></div><div className="loading-text">加载中...</div></div>}>
                  <Component />
                </React.Suspense>
              </ProtectedRoute>
            );
          } else {
            element = (
              <React.Suspense fallback={<div className="loading-screen"><div className="loading-spinner"></div><div className="loading-text">加载中...</div></div>}>
                <Component />
              </React.Suspense>
            );
          }

          return (
            <Route
              key={route.path}
              path={route.path}
              element={element}
            />
          );
        })}
        <Route path="*" element={<Navigate to="/quick-rectification" replace />} />
      </Routes>
    </Router>
  );
};

const App: React.FC = () => {
  // 预加载移动端组件
  React.useEffect(() => {
    preloadMobileComponents();
  }, []);

  message.config({ maxCount: 3 });

  return (
    <ConfigProvider
      locale={zhCN}
      theme={{
        hashed: false,
        cssVar: true,
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: '#667eea',
          colorPrimaryBg: '#f0f3ff',
          colorPrimaryBgHover: '#e0e7ff',
          colorPrimaryBorder: '#c7d2fe',
          colorPrimaryBorderHover: '#a5b4fc',
          colorPrimaryHover: '#5a6fd6',
          colorPrimaryActive: '#4f46e5',
          colorPrimaryText: '#667eea',
          colorPrimaryTextHover: '#5a6fd6',
          colorPrimaryTextActive: '#4f46e5',
          borderRadius: 8,
          borderRadiusLG: 12,
          borderRadiusSM: 6,
          colorLink: '#667eea',
          colorLinkHover: '#5a6fd6',
          colorLinkActive: '#4f46e5',
        },
        components: {
          Button: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Input: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Checkbox: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Radio: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Switch: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Menu: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Tabs: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Tag: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Table: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Pagination: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Select: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          DatePicker: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Upload: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Progress: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Alert: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Message: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
          Notification: {
            colorPrimary: '#667eea',
            algorithm: true,
          },
        }
      }}
    >
      <AntApp>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
};

export default App;
